import { useEffect, useRef, useState } from 'react';
import isFunction from 'lodash/isFunction';

// The value passed in the current render will be returned in the next render
// Example:
//   const previousValue = usePrevious(currentValue);
// https://reactjs.org/docs/hooks-faq.html#how-to-get-the-previous-props-or-state
export function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
}

// Runs `callback` every time the ref changes
// Example:
//   const ref = useRefEffect(node => {...});
//   <div ref={ref} />
export function useRefEffect(callback, dependencies = []) {
  const [node, setNode] = useState(null);
  useEffect(() => {
    if (node) {
      callback(node);
    }
  }, [node, ...dependencies]);
  return setNode;
}

// Runs `callback` when the ref is set the first time
// Example:
//   const ref = useRefDidMount(node => {...});
//   <div ref={ref} />
export function useRefDidMount(callback) {
  const [used, setUsed] = useState(false);
  const [node, setNode] = useState(null);
  useEffect(() => {
    if (used) {
      return;
    }
    if (node) {
      callback(node);
      setUsed(true);
    }
  }, [node]);
  return setNode;
}

// TODO: https://github.com/palmerhq/the-platform#usewindowscrollposition
export function useWindowScrollPos() {
  const [scrollPos, setScrollPos] = useState([0, 0]);
  const [waitingForAnimationFrame, setWaitingForAnimationFrame] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const { scrollX, scrollY } = window;

      if (!waitingForAnimationFrame) {
        window.requestAnimationFrame(() => {
          setScrollPos([scrollX, scrollY]);
          setWaitingForAnimationFrame(false);
        });

        setWaitingForAnimationFrame(true);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return scrollPos;
}

export function useEffectDidUpdate(effect, dependencies = []) {
  const didMountRef = useRef(false);
  useEffect(() => {
    if (didMountRef.current) {
      effect();
    } else {
      didMountRef.current = true;
    }
  }, dependencies);
}

// This hook runs `fn` *before* a component mounts as opposed to after like useEffect does.
// This is analogous to `componentWillMount` on class components
export function useBeforeMount(fn, dependencies = []) {
  // This symbol is used as as semaphore or flag to determine if `fn` has been run yet.
  const semaphore = Symbol('useBeforeMount');

  // Set the symbol as the initial value on this ref
  const onUnmount = useRef(semaphore);

  // If the current value of the ref is the same as the semaphore,
  // we know the fn hasn't been run yet
  if (onUnmount.current === semaphore) {
    // Run the fn, setting the value of the ref to its return value.
    // If the return value is a function, it will be used as a 'teardown' handler,
    // similar to returning a function in `useEffect`.
    onUnmount.current = fn();
  }

  // If our dependencies change, re-run fn, similar to useEffect
  useEffect(() => {
    if (onUnmount.current !== semaphore) {
      onUnmount.current = fn();
    }
  }, dependencies);

  // When unmounting, if the return value from the fn is a function, run it
  useEffect(
    () => () => {
      if (isFunction(onUnmount.current)) {
        onUnmount.current();
      }
    },
    [onUnmount.current]
  );
}
